"use client";

import { useIsMobile } from "hooks";
import * as React from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  type YAxisProps,
} from "recharts";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ToggleGroup,
  ToggleGroupItem,
} from "visuais";

export type ChartDataItem = {
  date: string;
} & {
  [key: string]: number | string;
};

type ChartAreaInteractiveProps = {
  chartData: ChartDataItem[];
  chartConfig: ChartConfig;
  chartTitle: string;
  chartDesc: string;
  yAxes?: YAxisProps[];
  periodo?: "7d" | "30d" | "90d";
  setPeriodo?: (periodo: "7d" | "30d" | "90d") => void;
  status?: "pending" | "success" | "error";
  error?: { message?: string };
};

export function ChartAreaInteractive({
  chartData = [],
  chartConfig = {},
  chartTitle = "",
  chartDesc = "",
  yAxes = [{ yAxisId: "left", orientation: "left" }],
  periodo = "7d",
  setPeriodo,
  status = "pending",
  error = { message: "" },
}: ChartAreaInteractiveProps) {
  const isMobile = useIsMobile();

  React.useEffect(() => {
    if (isMobile && setPeriodo && periodo !== "7d") {
      setPeriodo("7d");
    }
  }, [isMobile, periodo, setPeriodo]);

  const dias = periodo === "30d" ? 30 : periodo === "7d" ? 7 : 90;

  const filteredData = Array.isArray(chartData)
    ? chartData.filter((item) => {
        const date = new Date(item.date);
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - dias);
        return date >= startDate;
      })
    : [];

  return (
    <Card className="@container/card bg-input/40 backdrop-blur-2xl w-full">
      <CardHeader>
        <CardTitle>{chartTitle}</CardTitle>
        <CardDescription>
          <span className="hidden @[540px]/card:block">{chartDesc}</span>
        </CardDescription>
        <CardAction>
          <ToggleGroup
            type="single"
            value={periodo}
            onValueChange={(v) => v && setPeriodo?.(v as "7d" | "30d" | "90d")}
            variant="outline"
            className="hidden *:data-[slot=toggle-group-item]:!px-4 @[767px]/card:flex"
          >
            <ToggleGroupItem value="90d">3 meses</ToggleGroupItem>
            <ToggleGroupItem value="30d">30 dias</ToggleGroupItem>
            <ToggleGroupItem value="7d">7 dias</ToggleGroupItem>
          </ToggleGroup>
        </CardAction>
      </CardHeader>

      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6 min-h-[250px] flex justify-center items-center">
        {status === "pending" && (
          <div className="text-muted-foreground text-sm">
            Carregando gráfico...
          </div>
        )}

        {status === "error" && (
          <div className="text-destructive text-sm text-center">
            Erro ao carregar os dados.
            <br />
            {error?.message ?? ""}
          </div>
        )}

        {status !== "pending" && status !== "error" && (
          <>
            {filteredData.length === 0 ? (
              <div className="text-muted-foreground text-sm text-center py-8 w-full">
                Não há dados no período selecionado!
              </div>
            ) : (
              <ChartContainer
                config={chartConfig}
                className="aspect-auto h-[250px] w-full"
              >
                <AreaChart
                  data={filteredData}
                  margin={{ top: 0, right: 0, left: 20, bottom: 0 }}
                >
                  <defs>
                    {Object.entries(chartConfig ?? {}).map(([key, config]) => (
                      <linearGradient
                        key={key}
                        id={`fill-${key}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={config.color ?? "var(--primary)"}
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor={config.color ?? "var(--primary)"}
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    ))}
                  </defs>

                  <CartesianGrid vertical={false} />

                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={32}
                    tickFormatter={(value) => {
                      try {
                        const date = new Date(value);
                        return date.toLocaleDateString("pt-BR", {
                          month: "short",
                          day: "numeric",
                        });
                      } catch {
                        return value;
                      }
                    }}
                  />

                  {(Array.isArray(yAxes) ? yAxes : []).map((yAxis, index) => (
                    <YAxis key={index} {...yAxis} domain={[0, "auto"]} />
                  ))}

                  <ChartTooltip
                    cursor={false}
                    defaultIndex={isMobile ? -1 : 10}
                    content={
                      <ChartTooltipContent
                        className="min-w-[200px] max-w-[250px]"
                        labelFormatter={(value: string) => {
                          try {
                            return new Date(value).toLocaleDateString("pt-BR", {
                              month: "short",
                              day: "numeric",
                            });
                          } catch {
                            return value;
                          }
                        }}
                        indicator="dot"
                      />
                    }
                  />

                  {Object.keys(chartConfig ?? {}).map((key) => {
                    const config = chartConfig[key];
                    if (!config) return null;
                    return (
                      <Area
                        key={key}
                        dataKey={key}
                        yAxisId={config.yAxisId ?? "left"}
                        type="monotone"
                        stroke={config.color ?? "var(--primary)"}
                        fillOpacity={1}
                        fill={`url(#fill-${key})`}
                        stackId="a"
                      />
                    );
                  })}
                </AreaChart>
              </ChartContainer>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
